import gql from 'graphql-tag';

import { showErrorBar } from 'actions/errorBar';
import {
  populateWorkspaces,
  setPendingInvites,
  updateWorkspaceAdmins,
  updateWorkspaceDetails,
} from 'actions/workspace';
import { ERROR } from 'constants/error-severities';
import { getGraphQLClient } from 'utilities/graphql';
import * as loggerx from 'utilities/loggerx';
import * as sentry from 'utilities/sentry';

import declineWorkspaceInvitation from './declineWorkspaceInvitation.graphql';
import getPendingWorkspaceInvitesForUser from './getPendingWorkspaceInvitations.graphql';
import GetUserWorkspaceMemberships from './getUserWorkspaceMemberships.graphql';
import getWorkspaceBillingDetails from './getWorkspaceBillingDetails.graphql';

export function fetchWorkspaces(fetchPolicy = 'no-cache') {
  return dispatch => {
    const client = getGraphQLClient();

    client
      .query({
        query: GetUserWorkspaceMemberships,
        fetchPolicy,
      })
      .then(({ data }) => {
        const { userWorkspaceMemberships } = data;

        const memberships = userWorkspaceMemberships.map(membership => {
          const { organization: org } = membership;
          const {
            total_active_admins,
            total_active_creators,
            total_workspace_users,
          } = org.counts.users;

          const totalAdminCount = total_active_admins;

          const totalActiveCreators = totalAdminCount + total_active_creators;

          const members = total_workspace_users;

          return {
            name: org.name,
            id: org.id,
            members,
            totalActiveCreators,
            totalAdminCount,
            memberRole: membership.member_role,
            memberJoined: membership.createdAt,
            type: org.type,
            isSelected: membership.isSelected,
            workspaceLogoPath: org.workspaceLogoPath,
            onboarding: membership.onboarding,
            was_loom_user_before_membership:
              membership.was_loom_user_before_membership,
            organization_properties: org.organization_properties,
            organization_id: org.id,
            limits: org.limits,
            counts: org.counts,
            memberRoles: org.member_roles,
            memberLimits: membership.member_limits,
          };
        });

        dispatch(populateWorkspaces([...memberships]));
      })
      .catch(error => {
        dispatch(showErrorBar(ERROR, 'Error fetching user workspaces'));
        sentry.logException(error, 'unable to fetch user workspaces');
      });
  };
}

export function fetchBillingDetailsForWorkspace(workspaceId) {
  return dispatch => {
    const client = getGraphQLClient();

    client
      .query({
        query: getWorkspaceBillingDetails,
        variables: {
          workspaceId,
        },
        fetchPolicy: 'no-cache',
      })
      .then(({ data }) => {
        const { billing_details } = data.billing || {};

        dispatch(
          updateWorkspaceDetails({
            id: workspaceId,
            billing_details,
          })
        );
      })
      .catch(error => {
        sentry.logException(error, 'unable to fetch user workspace details');
      });
  };
}

export function fetchPendingWorkspaceInvites() {
  return dispatch => {
    const client = getGraphQLClient();

    client
      .query({
        query: getPendingWorkspaceInvitesForUser,
      })
      .then(({ data }) => {
        dispatch(setPendingInvites([...data?.invites?.pendingInvites]));
      })
      .catch(err => {
        dispatch(
          showErrorBar(ERROR, 'Error fetching pending workspace invites')
        );
        sentry.logException(err, 'unable to fetch pending workspace invites');
      });
  };
}

export function declinePendingWorkspaceInvite(inviteId) {
  return (dispatch, getState) => {
    const invites = getState().workspace.pendingInvites;
    const client = getGraphQLClient();

    // optimistically remove the invite
    dispatch(
      setPendingInvites([...invites.filter(invite => invite.id !== inviteId)])
    );

    client
      .mutate({
        mutation: declineWorkspaceInvitation,
        variables: { inviteId },
      })
      .then(({ data }) => {
        if (!data.declineWorkspaceInvitation.success) {
          dispatch(setPendingInvites([...invites]));
        }
      })
      .catch(err => {
        dispatch(setPendingInvites([...invites]));
        dispatch(showErrorBar(ERROR, 'Error declining workspace invite'));
        sentry.logException(err, 'unable to decline workspace invite');
      });
  };
}

export function fetchWorkspaceAdmins(workspaceId) {
  return async dispatch => {
    const client = getGraphQLClient();
    const GET_ADMINS_QUERY = gql`
      query ($orgId: ID!) {
        orgAdmins: getOrganizationAdmins(orgId: $orgId) {
          ... on OrgAdmins {
            user {
              id
              email
              first_name
              last_name
            }
          }
          ... on Error {
            message
          }
        }
      }
    `;

    try {
      const { data } = await client.query({
        query: GET_ADMINS_QUERY,
        variables: { orgId: workspaceId },
        fetchPolicy: 'no-cache',
      });

      dispatch(updateWorkspaceAdmins(workspaceId, data.orgAdmins.user));
    } catch (err) {
      dispatch(showErrorBar(ERROR, 'Error fetching workspace admins'));
      loggerx.error(
        err,
        {
          workspaceId,
        },
        {
          team: loggerx.COMPANY_TEAM,
        }
      );
    }
  };
}
